import { Avatar, CardContent, Grid, Pagination, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcCard, MprcCardActions, MprcConfirmDialog, MprcContainer, MprcFormSubHeader, MprcIconButton, MprcIconButtonDropdown, MprcLink, MprcText } from "components";
import { MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcFilterLine } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { IconDelete } from "components/icons";
import { IconCircleEllipsis } from "components/icons/IconCircleEllipsis";
import { UserInfoDto, UserInfoDtoPaginatedList } from "generated/CoreApi_v1";
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////

type MenuOptions = "updateList" | "clear";

interface IProps {
  viewData?: UserInfoDtoPaginatedList;
  canDelete?: boolean;
  onClearAll: () => void;
  onDeleteUser: (user: UserInfoDto) => void;
  onChangePage: (pageNumber: number) => void;
  onReload: () => void;
  customFilter?: React.ReactNode;
}

export const BatchEditSelectedUsers: React.FC<IProps> = (props) => {

  const { viewData, onClearAll, onDeleteUser, onChangePage, onReload, canDelete = true, customFilter } = props;
  const { t } = useTranslation("lms");
  const { showUser } = useModalUserProfile();

  const [dialog, setDialog] = useState<any>();

  ///
  const getMenuItems = (): MprcDropdownItems<MenuOptions> => {
    let menuItems: MprcDropdownItems<MenuOptions> = [];

    if (viewData && viewData.totalRows > 0 && canDelete == true) {
      menuItems.push({ text: <MprcText color="muted">{t("Enrollment.StepOperation.Operation.UpdateListAction")}</MprcText>, value: "updateList" });

      menuItems.push({ text: <MprcText color="muted">{t("Enrollment.StepOperation.Operation.ClearAction")}</MprcText>, value: "clear" });
    }

    return menuItems;
  };

  ///
  const handleMenuOptions = (option: MenuOptions) => {
    switch (option) {
      case "updateList":
        onReload();
        break;
      case "clear":
        setDialog(
          <MprcConfirmDialog
            open
            severity="danger"
            message={<span>{t("Enrollment.StepOperation.Operation.ConfirmClearAllMessage")}</span>}
            onClose={
              (result) => {
                if (result) onClearAll();
                setDialog(null);
              }
            }
          />
        );
        break;
    }
  };

  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  return (
    <>
      {dialog}
      <MprcContainer>
        <MprcCard>
          <MprcCardFilterBox>
            <MprcFilterLine>
              <div>
                <MprcFormSubHeader
                  title={t("Enrollment.StepOperation.SelectedUsers.Title")}
                />
              </div>
              <div>
                <Grid>
                  {getMenuItems().length > 0 &&
                    <MprcIconButtonDropdown
                      color="muted"
                      icon={<IconCircleEllipsis size="sm" />}
                      separator="blank"
                      onChange={(selection) => handleMenuOptions(selection)}
                      items={getMenuItems()}
                    />
                  }
                </Grid>
              </div>
            </MprcFilterLine>
            {customFilter}
          </MprcCardFilterBox>

          <CardContent>
            {
              viewData &&
              <>
                <MprcContainer>
                  <Typography variant="caption" color="muted">
                    {
                      t(viewData.totalRows > 0
                        ? "Enrollment.StepOperation.SelectedUsers.AddUsersText"
                        : "Enrollment.StepOperation.SelectedUsers.UsersNotAddMessage",
                        { count: viewData.totalRows }
                      )
                    }
                  </Typography>
                </MprcContainer>
                <Grid container justifyContent="center" alignItems="center" spacing={1}>
                  {viewData.items.map((item) => {
                    return (
                      <Grid key={item.userId} size={{ xs: 12 }}>
                        <Grid container justifyContent="center" alignItems="center" spacing={1} wrap="nowrap">
                          <Grid>
                            <Avatar
                              className={AppGlobalClassKeys.clickable}
                              sx={{ width: 48, height: 48 }}
                              src={item.thumbnailUrl == null ? undefined : item.thumbnailUrl}
                              onClick={() => showUser(item.userId)}
                            />
                          </Grid>
                          <Grid size="grow" wrap="nowrap">
                            <MprcLink onClick={() => showUser(item.userId)}>
                              <Typography variant="h5" noWrap>
                                {item.name}
                              </Typography>
                            </MprcLink>
                          </Grid>
                          {canDelete == true &&
                            <Grid>
                              <MprcIconButton
                                size="small"
                                onClick={() => onDeleteUser(item)}
                              >
                                <IconDelete color="muted" />
                              </MprcIconButton>
                            </Grid>
                          }
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
                <MprcCardActions>
                  {viewData && viewData.totalPages > 1 && (
                    <Pagination
                      shape="rounded"
                      color="primary"
                      page={viewData.pageNumber}
                      count={viewData.totalPages}
                      onChange={(e, page) => onChangePage(page)}
                    />
                  )}
                </MprcCardActions>
              </>
            }
          </CardContent>
        </MprcCard>
      </MprcContainer>
    </>
  );
};
